import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GetTasksAndAlertsAnalyticsResponseData } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { PeriodFilterEnum } from '@/ui';
import { AnalyticsQueryFilter } from '../util/analytics-query-filter';
import { FETCH_TASKS_AND_ALERTS_QUERY_KEY } from './analytics-query-keys';

export type FetchTasksAndAlertsQuery = AnalyticsQueryFilter & {
    /** The bucket size the server groups the timeseries into. */
    unit?: PeriodFilterEnum;
};

export type FetchTasksAndAlertsResult = {
    data: GetTasksAndAlertsAnalyticsResponseData | undefined;
    status: 'error' | 'success' | 'pending';
    refetch: () => void;
};

/**
 * The tasks-and-alerts timeseries, plus the day-of-week totals that come with
 * it. Every chart on the reports page bar the activity tables and the heatmap
 * is derived from this one read.
 */
export function useFetchTasksAndAlerts(
    query: FetchTasksAndAlertsQuery
): FetchTasksAndAlertsResult {
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const { status, data, refetch } = useQuery({
        queryKey: [FETCH_TASKS_AND_ALERTS_QUERY_KEY, query],
        queryFn: () => {
            return openApiRef.current.analytics
                .getTasksAndAlerts(query)
                .then((response) => response.data.data);
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: 0
    });

    return {
        status,
        data,
        refetch: () => {
            void refetch();
        }
    };
}
